import React, { useContext } from 'react';
import HeroSection from '../components/HeroSection';
import PricingCard from '../components/PricingCard';
import TestimonialCard from '../components/TestimonialCard';
import { CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ModalContext } from '../App';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import '../components/ServicePageLayout.css';

const CompanyFormation = () => {
  const { openModal } = useContext(ModalContext);
  const [openFaq, setOpenFaq] = React.useState(null);

  const heroData = {
    eyebrow: 'COMPANY FORMATION',
    title: 'Launch Your Business Right',
    description: 'Professional company formation services to establish your business entity with proper legal structure and protection.',
    backgroundImage: 'https://images.pexels.com/photos/5816283/pexels-photo-5816283.jpeg',
    showForm: false,
    ctaText: 'Form Your Company',
    ctaLink: '#pricing'
  };

  const processSteps = [
    {
      title: 'Business Structure Selection',
      description: 'Our business formation experts guide you in selecting the optimal legal structure (LLC, Corporation, Partnership) based on your specific business goals, liability concerns, and tax considerations. We evaluate your unique situation to recommend the entity type that provides the best protection and benefits for your venture.'
    },
    {
      title: 'Documentation Preparation',
      description: 'We prepare all required formation documents including Articles of Incorporation or Organization, Operating Agreements, Corporate Bylaws, and necessary state-specific filings. Our team ensures every document is professionally drafted, legally compliant, and customized to reflect your business structure and operational requirements.'
    },
    {
      title: 'Filing and Registration',
      description: 'We handle the complete filing process with state authorities, obtain your Federal EIN from the IRS, and ensure all compliance requirements are met. You receive your official formation documents, business certificates, and ongoing support to maintain your entity in good standing with state and federal requirements.'
    }
  ];

  const pricingPlans = [
    { 
      name: 'Starter', 
      price: '199', 
      features: [
        'Complete Business Entity Formation',
        'Articles of Incorporation/Organization Filing',
        'State Filing Fees Fully Included',
        'Federal EIN Tax ID Registration',
        'Standard Operating Agreement Template',
        'First Year Registered Agent Service',
        'Corporate Records Book Setup',
        'Business Formation Certificate',
        'Email Confirmation and Tracking',
        'Basic Compliance Guidelines',
        'State Filing Status Updates',
        'New Business Setup Checklist'
      ], 
      isPopular: true 
    },
    { 
      name: 'Professional', 
      price: '399', 
      features: [
        'Everything in Starter Package',
        'Custom Operating Agreement Drafting',
        'Initial Board of Directors Resolutions',
        'Stock Certificates with Corporate Seal',
        'Banking Resolution for Account Opening',
        'S-Corporation Tax Election Filing',
        'Lifetime Registered Agent Service',
        'Annual Compliance Calendar System',
        'Organizational Meeting Minutes',
        'Corporate Bylaws Preparation',
        'Shareholder Agreement Template',
        'Business License Research Report',
        'Dedicated Formation Specialist',
        'Priority Processing and Filing',
        'Complete Corporate Document Package'
      ] 
    }
  ];

  const whyChooseData = {
    paragraphs: [
      'Establishing the right business entity is fundamental to your company success and long-term protection. At Unity Trademark, we combine legal expertise with practical business knowledge to help you form your company correctly from day one. Our experienced business formation specialists have assisted thousands of entrepreneurs in launching their ventures with proper legal structure and compliance.',
      'We understand that choosing the right entity type and navigating formation requirements can be complex. That is why we have simplified the process while maintaining the highest standards of legal accuracy. From sole proprietorships to multi-member LLCs and corporations, we provide comprehensive formation services that set your business up for sustainable growth and success.'
    ],
    features: [
      {
        title: 'Expert Entity Selection Guidance',
        description: 'Our business formation specialists help you choose between LLC, Corporation, or other structures based on your specific needs, tax situation, and long-term business goals. We ensure you start with the right foundation.'
      },
      {
        title: 'Complete Documentation Services',
        description: 'We handle all paperwork including Articles of Organization, Operating Agreements, Corporate Bylaws, and state filings. Every document is professionally prepared and customized for your business structure.'
      },
      {
        title: 'Fast Processing and Filing',
        description: 'Our streamlined process ensures quick turnaround times for all state filings and federal registrations. We monitor your application status and keep you informed throughout the entire formation process.'
      },
      {
        title: 'Ongoing Compliance Support',
        description: 'Beyond formation, we provide guidance on maintaining your entity in good standing, including annual report requirements, registered agent services, and compliance calendar management.'
      }
    ]
  };

  const testimonials = [
    { name: 'David Kim', company: 'Tech Startup Founder', text: 'Forming my LLC was incredibly smooth with Unity Trademark. They explained everything clearly and handled all the paperwork professionally. My company was up and running in no time!', image: 'https://i.pravatar.cc/150?img=12' },
    { name: 'Lisa Anderson', company: 'E-commerce Entrepreneur', text: 'I appreciated their guidance on choosing between LLC and Corporation. They helped me understand the tax implications and set up my business structure perfectly for future growth.', image: 'https://i.pravatar.cc/150?img=5' },
    { name: 'James Wilson', company: 'Restaurant Owner', text: 'The comprehensive package made everything easy. From EIN registration to operating agreements, everything was handled professionally. Highly recommend their services!', image: 'https://i.pravatar.cc/150?img=13' },
    { name: 'Maria Garcia', company: 'Consultant', text: 'Great service at a fair price. They took care of all the state filings and provided excellent ongoing support. My business formation was stress-free.', image: 'https://i.pravatar.cc/150?img=47' },
    { name: 'Robert Chen', company: 'Real Estate Investor', text: 'Professional and efficient. They helped me set up multiple LLCs for my investment properties and made sure everything was compliant with state requirements.', image: 'https://i.pravatar.cc/150?img=15' }
  ];

  const faqs = [
    {
      question: 'What is the difference between an LLC and a Corporation?',
      answer: 'An LLC offers flexible management structure, pass-through taxation, and simpler compliance requirements, making it ideal for small to medium businesses. A Corporation provides more formalized structure, easier ability to raise capital through stock issuance, and potential tax advantages for larger businesses. Our specialists help you evaluate which structure best suits your specific business needs and goals.'
    },
    {
      question: 'How long does the company formation process take?',
      answer: 'Formation timeline varies by state, but most filings are processed within 5-15 business days. We offer expedited filing services for faster processing. Once your formation documents are approved, you receive your official certificate and can begin operating your business immediately.'
    },
    {
      question: 'Do I need a registered agent, and what do they do?',
      answer: 'Yes, most states require businesses to have a registered agent - a person or company designated to receive legal documents and official correspondence on behalf of your business. Our packages include registered agent service to ensure you never miss important legal notices or compliance deadlines.'
    },
    {
      question: 'What is an EIN and do I need one?',
      answer: 'An Employer Identification Number (EIN) is a federal tax ID issued by the IRS. You need an EIN to open business bank accounts, hire employees, and file business tax returns. We handle the complete EIN application process as part of your formation package, ensuring your business is properly registered with federal authorities.'
    },
    {
      question: 'What ongoing compliance requirements do I have after formation?',
      answer: 'Requirements vary by state and entity type but typically include annual reports, franchise tax payments, and maintaining current registered agent information. Some states also require periodic statement filings. We provide compliance calendars and ongoing support to help you meet all state and federal requirements and maintain your entity in good standing.'
    }
  ];

  return (
    <div className="service-page">
      <HeroSection {...heroData} />

      <section className="section">
        <div className="container">
          <h2 className="section-title">About Company Formation</h2>
          <p className="section-subtitle">
            Company formation is the legal process of creating a business entity that provides liability protection, tax benefits, and professional credibility. By establishing a formal business structure such as an LLC or Corporation, you separate your personal assets from business obligations, gain access to business banking and credit, and create a foundation for sustainable growth. Proper formation also ensures compliance with state and federal regulations while positioning your business for long-term success.
          </p>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">How We Work</h2>
          <p className="section-subtitle">
            Our streamlined three-step process makes company formation simple and efficient. From entity selection to final registration, we handle every detail with professional expertise and personalized attention.
          </p>
          <div className="process-grid">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{String(index + 1).padStart(2, '0')}</div>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="section">
        <div className="container">
          <h2 className="section-title">Choose Your Package</h2>
          <p className="section-subtitle">
            Select the company formation package that best fits your business needs and budget. All packages include professional service, expert guidance, and comprehensive support throughout the formation process.
          </p>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <PricingCard 
                key={index} 
                {...plan} 
                onGetStarted={(packageName) => openModal(`Company Formation - ${packageName}`)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Why Choose Unity Trademark</h2>
          <div className="why-choose-content">
            {whyChooseData.paragraphs.map((paragraph, index) => (
              <p key={index} className="why-choose-paragraph">{paragraph}</p>
            ))}
          </div>
          <div className="features-grid">
            {whyChooseData.features.map((feature, index) => (
              <div key={index} className="feature-card">
                <CheckCircle size={40} className="feature-icon-svg" />
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Form Your Company?</h2>
            <p className="cta-description">
              Get started with professional company formation services starting at just $199. Join thousands of entrepreneurs who trust Unity Trademark for comprehensive business formation and legal compliance.
            </p>
            <Button 
              size="lg" 
              className="cta-button"
              onClick={() => openModal('Company Formation')}
            >
              Start Your Company Formation
            </Button>
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <h2 className="section-title">Client Success Stories</h2>
          <div className="testimonials-swiper-container">
            <Swiper
              modules={[Autoplay, Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
              }}
              loop={true}
              breakpoints={{
                640: { slidesPerView: 1, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 25 },
                1024: { slidesPerView: 3, spaceBetween: 30 },
              }}
              className="testimonials-swiper"
            >
              {testimonials.map((testimonial, index) => (
                <SwiperSlide key={index}>
                  <TestimonialCard {...testimonial} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-container">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openFaq === index ? '−' : '+'}</span>
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompanyFormation;